import type { ReploProject } from "schemas/generated/project";

import { skipToken } from "@tanstack/react-query";
import { productRouter } from "@v2/trpc/routes/product";

type UseShopifyProductEligibilityInput = {
  projectId: string | undefined;
  project: ReploProject | undefined;
};

type UseShopifyProductEligibilityResult = {
  isEligibleForShopifyProducts: boolean;
  isLoading: boolean;
};

export function useShopifyProductEligibility({
  projectId,
  project,
}: UseShopifyProductEligibilityInput): UseShopifyProductEligibilityResult {
  // Use project context for Stripe integration info instead of separate queries
  const stripeIntegration = project?.integrations?.stripe?.integration;
  const isStripeFullyOnboarded = stripeIntegration?.onboardingComplete ?? false;

  const { data: reploProductCount, isLoading: isLoadingReploProductCount } =
    productRouter.countByReferenceType.useQuery(
      projectId ? { projectId, referenceType: "REPLO" } : skipToken,
    );

  const hasReploProducts = (reploProductCount ?? 0) > 0;

  // Note (Ezra, 2026-01-21): If they have Replo products but have never been
  // onboarded to Stripe, these products must be starter site placeholders. In
  // that case, we should show the callout to connect Shopify so they can sync
  // real products and replace the placeholders.
  return {
    isEligibleForShopifyProducts: !isStripeFullyOnboarded && hasReploProducts,
    isLoading: isLoadingReploProductCount,
  };
}
